import type { DateRange } from "./date-range";

/**
 * Dashboard KPI wording.
 *
 * The one thing to understand here: an `AttendanceRecord` is unique per
 * `(employee, date)`, and the backend summary **counts rows**. So over a
 * multi-day range "Checked In" is a count of **employee-days**, not of people —
 * three check-ins spread over two days reads as `3`.
 *
 * `totalEmployees`, by contrast, is a **point-in-time** headcount that
 * deliberately ignores the date range. Dividing one by the other is therefore
 * only meaningful for a **single-day** range; over 30 days it compares a month
 * of records against today's headcount and can sail past 100 %.
 *
 * That ratio was previously shown for every range, which is how "3 checked in"
 * came to be captioned "100 % of workforce" for a 30-day window.
 */

/** Inclusive day count of a range. `2026-07-01 → 2026-07-30` is 30. */
export function rangeDayCount(range: DateRange): number {
  const from = Date.parse(`${range.from}T00:00:00Z`);
  const to = Date.parse(`${range.to}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return 0;
  return Math.round((to - from) / 86_400_000) + 1;
}

export function isSingleDay(range: DateRange): boolean {
  return range.from === range.to;
}

/**
 * Percentage of the workforce that attended — **only defined for a single
 * day**, where there is at most one record per employee. Null otherwise, and
 * null when there are no employees (rather than dividing by zero).
 */
export function attendanceRatePct(
  range: DateRange,
  checkedIn: number,
  totalEmployees: number,
): number | null {
  if (!isSingleDay(range) || totalEmployees <= 0) return null;
  return Math.round((checkedIn / totalEmployees) * 100);
}

/**
 * Caption under the Checked-In / Checked-Out tiles: the share of the workforce
 * on a single day, or an honest "how wide is this window" for a range — never a
 * percentage the figures can't support.
 */
export function attendanceHint(
  range: DateRange,
  checkedIn: number,
  totalEmployees: number,
): string {
  const pct = attendanceRatePct(range, checkedIn, totalEmployees);
  if (pct !== null) return `${pct}% of workforce`;
  const days = rangeDayCount(range);
  return days > 0 ? `across ${days} days` : "";
}
